import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { usersAPI } from "../../services/api";
import {
  Users, UserPlus, Shield, Edit, Trash2,
  Search, MoreVertical, Check, X,
  Clock, Activity, Lock, Unlock,
} from "lucide-react";

interface Permission {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

interface UserType {
  id: number;
  full_name: string;
  username: string;
  email: string;
  phone: string;
  role: "admin" | "manager" | "user";
  status: "active" | "inactive";
  avatar: string | null;
  last_login: string;
  join_date: string;
  permissions: Record<string, Permission>;
}

const defaultPermissions = {
  dashboard: { view: true,  add: false, edit: false, delete: false },
  finance:   { view: false, add: false, edit: false, delete: false },
  hr:        { view: false, add: false, edit: false, delete: false },
  customers: { view: false, add: false, edit: false, delete: false },
  vendors:   { view: false, add: false, edit: false, delete: false },
  warehouse: { view: false, add: false, edit: false, delete: false },
  production:{ view: false, add: false, edit: false, delete: false },
  reports:   { view: false, add: false, edit: false, delete: false },
  chat:      { view: true,  add: true,  edit: false, delete: false },
  backup:    { view: false, add: false, edit: false, delete: false },
};

const pageLabels: Record<string, string> = {
  dashboard:  "صفحه اصلی",
  finance:    "مدیریت مالی",
  hr:         "منابع انسانی",
  customers:  "مدیریت مشتریان",
  vendors:    "مدیریت فروشندگان",
  warehouse:  "مدیریت گودام",
  production: "مدیریت تولید",
  reports:    "گزارشات",
  chat:       "گفتگو",
  backup:     "بکاپ",
};

export default function UsersPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  const [users, setUsers]               = useState<UserType[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPermModal, setShowPermModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [search, setSearch]             = useState("");
  const [filterRole, setFilterRole]     = useState("all");
  const [menuOpen, setMenuOpen]         = useState<number | null>(null);
  const [logs, setLogs]                 = useState<any[]>([]);
  const [toast, setToast]               = useState("");

  const [newUser, setNewUser] = useState({
    fullName: "", username: "", email: "", phone: "",
    password: "", role: "user" as "admin" | "manager" | "user",
    permissions: { ...defaultPermissions },
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await usersAPI.list();
      if (res.success) setUsers(res.users);
    } catch {
      showToast("خطا در اتصال به سرور");
    }
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const filtered = users.filter(u => {
    const matchSearch = u.full_name.includes(search) || u.username.includes(search) || (u.email || "").includes(search);
    const matchRole   = filterRole === "all" || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleLabel = (r: string) => r === "admin" ? "ادمین" : r === "manager" ? "مدیر" : "کاربر";
  const roleColor = (r: string) => r === "admin" ? "#f59e0b" : r === "manager" ? "#3b82f6" : "#10b981";

  const handleAddUser = async () => {
    if (!newUser.fullName || !newUser.username || !newUser.password) {
      showToast("نام کامل، نام کاربری و پسورد الزامی است");
      return;
    }
    try {
      const res = await usersAPI.add(newUser);
      if (res.success) {
        showToast("✅ کاربر با موفقیت اضافه شد");
        setShowAddModal(false);
        setNewUser({ fullName: "", username: "", email: "", phone: "", password: "", role: "user", permissions: { ...defaultPermissions } });
        loadUsers();
      } else {
        showToast("❌ " + res.message);
      }
    } catch {
      showToast("❌ خطا در اتصال به سرور");
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      const res = await usersAPI.delete(id);
      if (res.success) {
        showToast("✅ کاربر حذف شد");
        loadUsers();
      } else {
        showToast("❌ " + res.message);
      }
    } catch {
      showToast("❌ خطا در اتصال به سرور");
    }
    setMenuOpen(null);
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const res = await usersAPI.toggleStatus(id);
      if (res.success) {
        showToast("✅ وضعیت کاربر تغییر کرد");
        loadUsers();
      } else {
        showToast("❌ " + res.message);
      }
    } catch {
      showToast("❌ خطا در اتصال به سرور");
    }
    setMenuOpen(null);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    try {
      const res = await usersAPI.updatePermissions(selectedUser.id, selectedUser.permissions);
      if (res.success) {
        showToast("✅ دسترسی‌ها ذخیره شد");
        setShowPermModal(false);
        loadUsers();
      } else {
        showToast("❌ " + res.message);
      }
    } catch {
      showToast("❌ خطا در اتصال به سرور");
    }
  };

  const handleShowLogs = async (user: UserType) => {
    setSelectedUser(user);
    setShowLogModal(true);
    try {
      const res = await usersAPI.logs(user.id);
      if (res.success) setLogs(res.logs);
    } catch {
      setLogs([]);
    }
  };

  const cardStyle = {
    borderRadius: 16, padding: "20px",
    background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.95)",
    border: `1px solid ${T.cardBorder}`,
    backdropFilter: "blur(12px)",
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10, color: T.text, fontSize: 13,
    fontFamily: "inherit", direction: "rtl" as const, outline: "none",
    boxSizing: "border-box" as const,
  };

  const modalBg = {
    position: "fixed" as const, inset: 0, zIndex: 100,
    background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  };

  const modalCard = {
    width: "100%", maxWidth: 600, maxHeight: "90vh",
    borderRadius: 20, padding: "32px",
    background: dark ? "#0f0f2e" : "#ffffff",
    border: `1px solid ${T.cardBorder}`,
    boxShadow: "0 32px 100px rgba(0,0,0,0.5)",
    overflowY: "auto" as const,
  };

  return (
    <div style={{ padding: "28px 28px 40px" }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
          zIndex: 999, padding: "12px 24px", borderRadius: 12,
          background: dark ? "#1e293b" : "#fff",
          border: `1px solid ${T.cardBorder}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          color: T.text, fontSize: 13, fontWeight: 600,
          fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>⚙️ مدیریت سیستم</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>مدیریت کاربران</h1>
          <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>کنترل دسترسی و مدیریت کاربران سیستم</p>
        </div>
        <button onClick={() => setShowAddModal(true)} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "11px 20px", borderRadius: 12,
          background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
          border: "none", color: "white", fontSize: 13, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
          boxShadow: "0 4px 16px rgba(59,130,246,0.4)",
        }}>
          <UserPlus size={16} /> افزودن کاربر
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 24 }}>
        {[
          { label: "کل کاربران",   value: users.length,                                    color: "#3b82f6", icon: Users  },
          { label: "ادمین‌ها",     value: users.filter(u => u.role === "admin").length,    color: "#f59e0b", icon: Shield },
          { label: "مدیران",       value: users.filter(u => u.role === "manager").length,  color: "#8b5cf6", icon: Lock   },
          { label: "کاربران فعال", value: users.filter(u => u.status === "active").length, color: "#10b981", icon: Activity },
        ].map((s, i) => (
          <div key={i} style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `${s.color}18`, border: `1px solid ${s.color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <s.icon size={18} color={s.color} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: T.subtext }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div style={{ ...cardStyle, marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 200, background: dark ? "rgba(255,255,255,0.05)" : "#f8faff", border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "9px 14px" }}>
          <Search size={14} color={T.subtext} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجو..." style={{ background: "none", border: "none", outline: "none", color: T.text, fontSize: 13, fontFamily: "inherit", direction: "rtl", width: "100%" }} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["all","admin","manager","user"].map(r => (
            <button key={r} onClick={() => setFilterRole(r)} style={{
              padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, fontFamily: "inherit",
              background: filterRole === r ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
              color: filterRole === r ? "white" : T.subtext,
            }}>
              {r === "all" ? "همه" : roleLabel(r)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={cardStyle}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: T.subtext }}>
            <div style={{ width: 36, height: 36, border: "3px solid rgba(59,130,246,0.2)", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            در حال بارگذاری...
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                  {["کاربر","نقش","ایمیل","آخرین ورود","وضعیت","عملیات"].map((h,i) => (
                    <th key={i} style={{ padding: "12px 16px", textAlign: "right", color: T.subtext, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => (
                  <tr key={user.id} style={{ borderBottom: `1px solid ${T.cardBorder}`, transition: "background 0.2s" }}
                    onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "white", flexShrink: 0, overflow: "hidden" }}>
                          {user.avatar ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : user.full_name.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: T.text }}>{user.full_name}</div>
                          <div style={{ fontSize: 11, color: T.subtext }}>@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${roleColor(user.role)}18`, color: roleColor(user.role), border: `1px solid ${roleColor(user.role)}30` }}>
                        {roleLabel(user.role)}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", color: T.subtext }}>{user.email || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.subtext }}>
                        <Clock size={12} />
                        <span style={{ fontSize: 12 }}>{user.last_login || "هنوز وارد نشده"}</span>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: user.status === "active" ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)", color: user.status === "active" ? "#10b981" : "#ef4444", border: user.status === "active" ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(239,68,68,0.2)" }}>
                        {user.status === "active" ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <button onClick={() => { setSelectedUser(user); setShowPermModal(true); }} title="دسترسی‌ها" style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", background: "rgba(139,92,246,0.12)", color: "#8b5cf6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Shield size={14} />
                        </button>
                        <button onClick={() => handleShowLogs(user)} title="لاگ فعالیت" style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", background: "rgba(59,130,246,0.12)", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Activity size={14} />
                        </button>
                        <div style={{ position: "relative" }}>
                          <button onClick={() => setMenuOpen(menuOpen === user.id ? null : user.id)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", color: T.subtext, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MoreVertical size={14} />
                          </button>
                          {menuOpen === user.id && (
                            <div style={{ position: "absolute", left: 0, top: 34, zIndex: 50, background: dark ? "#1e293b" : "#fff", border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "6px", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", minWidth: 160 }}>
                              <button onClick={() => handleToggleStatus(user.id)} style={{ width: "100%", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", color: user.status === "active" ? "#ef4444" : "#10b981", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, borderRadius: 7 }}>
                                {user.status === "active" ? <><Lock size={13} /> غیرفعال کردن</> : <><Unlock size={13} /> فعال کردن</>}
                              </button>
                              {user.id !== 1 && (
                                <button onClick={() => handleDeleteUser(user.id)} style={{ width: "100%", padding: "8px 12px", background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, borderRadius: 7 }}>
                                  <Trash2 size={13} /> حذف کاربر
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && !loading && (
              <div style={{ textAlign: "center", padding: "40px", color: T.subtext }}>
                <Users size={40} style={{ margin: "0 auto 12px", opacity: 0.3, display: "block" }} />
                <div>کاربری یافت نشد</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <div style={modalBg} onClick={() => setShowAddModal(false)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>افزودن کاربر جدید</h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.subtext }}>اطلاعات کاربر را وارد کنید</p>
              </div>
              <button onClick={() => setShowAddModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              {[
                { label: "نام کامل *",      key: "fullName",  placeholder: "مثال: احمد رضایی" },
                { label: "نام کاربری *",    key: "username",  placeholder: "مثال: ahmad123"   },
                { label: "ایمیل",           key: "email",     placeholder: "example@email.com" },
                { label: "شماره تلفن",      key: "phone",     placeholder: "0700000000"        },
                { label: "پسورد *",         key: "password",  placeholder: "حداقل ۸ کاراکتر"  },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: T.subtext, display: "block", marginBottom: 6 }}>{f.label}</label>
                  <input
                    type={f.key === "password" ? "password" : "text"}
                    value={(newUser as any)[f.key]}
                    onChange={e => setNewUser({ ...newUser, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    style={inputStyle}
                  />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: T.subtext, display: "block", marginBottom: 6 }}>نقش</label>
                <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value as any })}
                  style={{ ...inputStyle, background: dark ? "#1e293b" : "#f8faff", color: T.text }}>
                  <option value="user">کاربر عادی</option>
                  <option value="manager">مدیر</option>
                  <option value="admin">ادمین</option>
                </select>
              </div>
            </div>

            {newUser.role !== "admin" && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>🔐 دسترسی‌ها</div>
                <div style={{ borderRadius: 12, border: `1px solid ${T.cardBorder}`, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                        <th style={{ padding: "10px 14px", textAlign: "right", color: T.subtext, fontWeight: 600 }}>صفحه</th>
                        {["مشاهده","افزودن","ویرایش","حذف"].map(h => (
                          <th key={h} style={{ padding: "10px", textAlign: "center", color: T.subtext, fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(pageLabels).map(page => (
                        <tr key={page} style={{ borderTop: `1px solid ${T.cardBorder}` }}>
                          <td style={{ padding: "10px 14px", color: T.text, fontWeight: 500 }}>{pageLabels[page]}</td>
                          {(["view","add","edit","delete"] as const).map(perm => (
                            <td key={perm} style={{ padding: "10px", textAlign: "center" }}>
                              <div onClick={() => setNewUser({
                                ...newUser,
                                permissions: { ...newUser.permissions, [page]: { ...(newUser.permissions as any)[page], [perm]: !(newUser.permissions as any)[page][perm] } }
                              })} style={{
                                width: 20, height: 20, borderRadius: 5, cursor: "pointer",
                                background: (newUser.permissions as any)[page][perm] ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : "transparent",
                                border: `2px solid ${(newUser.permissions as any)[page][perm] ? "#3b82f6" : dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                margin: "0 auto", transition: "all 0.2s",
                              }}>
                                {(newUser.permissions as any)[page][perm] && <Check size={11} color="white" strokeWidth={3} />}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleAddUser} style={{ flex: 1, padding: "12px", background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", border: "none", borderRadius: 10, color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                ✅ افزودن کاربر
              </button>
              <button onClick={() => setShowAddModal(false)} style={{ padding: "12px 20px", background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", border: `1px solid ${T.cardBorder}`, borderRadius: 10, color: T.text, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERMISSIONS MODAL */}
      {showPermModal && selectedUser && (
        <div style={modalBg} onClick={() => setShowPermModal(false)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>دسترسی‌های {selectedUser.full_name}</h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.subtext }}>مدیریت سطح دسترسی کاربر</p>
              </div>
              <button onClick={() => setShowPermModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
            </div>
            <div style={{ borderRadius: 12, border: `1px solid ${T.cardBorder}`, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                    <th style={{ padding: "10px 14px", textAlign: "right", color: T.subtext, fontWeight: 600 }}>صفحه</th>
                    {["مشاهده","افزودن","ویرایش","حذف"].map(h => (
                      <th key={h} style={{ padding: "10px", textAlign: "center", color: T.subtext, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(pageLabels).map(page => (
                    <tr key={page} style={{ borderTop: `1px solid ${T.cardBorder}` }}>
                      <td style={{ padding: "10px 14px", color: T.text, fontWeight: 500 }}>{pageLabels[page]}</td>
                      {(["view","add","edit","delete"] as const).map(perm => (
                        <td key={perm} style={{ padding: "10px", textAlign: "center" }}>
                          <div onClick={() => {
                            const updated = { ...selectedUser, permissions: { ...selectedUser.permissions, [page]: { ...(selectedUser.permissions as any)[page], [perm]: !(selectedUser.permissions as any)[page][perm] } } };
                            setSelectedUser(updated);
                          }} style={{
                            width: 20, height: 20, borderRadius: 5, cursor: "pointer",
                            background: (selectedUser.permissions as any)[page]?.[perm] ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : "transparent",
                            border: `2px solid ${(selectedUser.permissions as any)[page]?.[perm] ? "#3b82f6" : dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            margin: "0 auto", transition: "all 0.2s",
                          }}>
                            {(selectedUser.permissions as any)[page]?.[perm] && <Check size={11} color="white" strokeWidth={3} />}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={handleSavePermissions} style={{ width: "100%", marginTop: 16, padding: "12px", background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", border: "none", borderRadius: 10, color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              ✅ ذخیره تغییرات
            </button>
          </div>
        </div>
      )}

      {/* LOG MODAL */}
      {showLogModal && selectedUser && (
        <div style={modalBg} onClick={() => setShowLogModal(false)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>لاگ فعالیت‌های {selectedUser.full_name}</h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.subtext }}>تاریخچه کامل فعالیت‌های کاربر</p>
              </div>
              <button onClick={() => setShowLogModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
            </div>
            {logs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: T.subtext }}>
                <Activity size={48} style={{ margin: "0 auto 16px", opacity: 0.3, display: "block" }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 8 }}>هنوز فعالیتی ثبت نشده</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {logs.map((log, i) => (
                  <div key={i} style={{ padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", border: `1px solid ${T.cardBorder}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#3b82f6" }}>{log.action}</span>
                      <span style={{ fontSize: 11, color: T.subtext }}>{log.created_at}</span>
                    </div>
                    <div style={{ fontSize: 12, color: T.text }}>{log.details}</div>
                    <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>IP: {log.ip}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}